import { captureApiError } from "@root/utils/sentry";
import axios, { isAxiosError } from "axios";
import { makeUseAxios } from "axios-hooks";
import { add } from "date-fns/add";
import { parseBreakLine } from "./tools/parsing/parseBreakLine";

const utf8ToB64 = (str: string) => {
  return window.btoa(unescape(encodeURIComponent(str)));
};

const sendErrorFeedback = (data: object) => {
  if ("errorMessage" in data && typeof data.errorMessage === "string") {
    window.publish("feedback", {
      severity: "error",
      message: parseBreakLine(data.errorMessage),
    });
  } else if ("message" in data && typeof data.message === "string") {
    window.publish("feedback", {
      severity: "error",
      message: parseBreakLine(data.message),
    });
  }
};

export const isLogged = () => {
  const isLogged = localStorage.getItem("isLogged");
  if (typeof isLogged === "string") {
    const dateIsLogged = new Date(isLogged);
    if (
      dateIsLogged.toString() !== "Invalid Date" &&
      add(dateIsLogged, { hours: 10 }).getTime() > new Date().getTime()
    ) {
      return true;
    }
  }
  return false;
};

export const buildBasicAxiosInstance = (url: string) => {
  return axios.create({
    baseURL: url,
    withCredentials: true,
    timeout: 29000,
    timeoutErrorMessage: "Timeout",
  });
};

export const buildAxiosInstance = (url: string) => {
  const axiosInstance = axios.create({
    baseURL: url,
    withCredentials: true,
    timeout: 29000,
    timeoutErrorMessage: "Timeout",
  });

  axiosInstance.interceptors.response.use(
    (res) => res,
    (error) => {
      const { pathname, origin } = window.location;
      const freePath = ["/v2/login", "/v2/login/forgotten", "/v2/login/recovery"];

      if (isAxiosError(error)) {
        captureApiError(error);
        const data: unknown = error.response?.data;
        if (error.message === "Timeout") {
          sendErrorFeedback({
            message:
              "Les informations ne peuvent pas être affichées pour le moment suite à un problème technique. Veuillez recharger la page.",
          });
        } else if (error.response?.status === 401) {
          if (!freePath.includes(pathname)) {
            localStorage.setItem("isLogged", "");
            window.location.href = `${origin}/v2/login${
              window.location.pathname
                ? `?redirection=${utf8ToB64(window.location.pathname)}&unauthorized=true`
                : "?unauthorized=true"
            }`;
          } else if (
            data !== null &&
            typeof data === "object" &&
            (!("code" in data) || data.code !== "ENTITY_NOT_FOUND")
          ) {
            sendErrorFeedback(data);
          }
        } else if (error.response?.status === 403) {
          window.publish("feedback", {
            severity: "error",
            message:
              "Vous n'êtes pas autorisé à effectuer cette action. Si vous pensez que c'est une erreur, veuillez contacter un administrateur",
          });
        } else if (
          data !== null &&
          typeof data === "object" &&
          (!("code" in data) || data.code !== "ENTITY_NOT_FOUND")
        ) {
          sendErrorFeedback(data);
        }
      }
      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

export const buildAxiosWithoutInterceptors = (url: string) => {
  const axiosInstance = buildBasicAxiosInstance(url);

  return makeUseAxios({
    cache: false,
    axios: axiosInstance,
    defaultOptions: { manual: true, ssr: false },
  });
};

export const buildMakeUseAxios = (url: string) => {
  const axiosInstance = buildAxiosInstance(url);

  return makeUseAxios({
    cache: false,
    axios: axiosInstance,
    defaultOptions: { manual: true, ssr: false },
  });
};
